import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import { getBrandInfo, getProductsByBrand } from '../data/brandsData';
import './BrandPage.css';

const BrandPage: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();

  // Validate brand ID and get brand info
  const brandName = brandId?.toUpperCase() as 'SKYSUN' | 'SKYTION';

  if (!brandId || !['skysun', 'skytion'].includes(brandId.toLowerCase())) {
    return <Navigate to="/brands" replace />;
  }

  const brand = getBrandInfo(brandName);
  const products = getProductsByBrand(brandName);

  return (
    <>
      <SEO
        title={`${brand.displayName} Brand - ${brand.tagline}`}
        description={brand.description}
        keywords={`${brand.name}, ${brand.displayName}, tissue products, ${brand.positioning}, ${brand.targetMarket.join(', ')}`}
      />

      {/* Hero Section */}
      <section className={`brand-hero-${brand.id} py-5`}>
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mx-auto brand-hero-content">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Badge bg="light" text="dark" className="mb-3 px-4 py-2">{brand.positioning}</Badge>
                <h1 className="display-2 mb-3 fw-bold">{brand.displayName}</h1>
                <p className="display-6 fst-italic mb-4">"{brand.tagline}"</p>
                <p className="lead mb-4">
                  {brand.description}
                </p>
                <div className="brand-certifications-list">
                  {brand.certifications?.map((cert, idx) => (
                    <Badge
                      key={idx}
                      bg="light"
                      text="dark"
                      className="px-3 py-2"
                    >
                      ✓ {cert}
                    </Badge>
                  ))}
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Target Markets */}
      <section className="target-markets-section py-5">
        <Container>
          <Row className="mb-4">
            <Col lg={8} className="mx-auto text-center">
              <h2 className="display-5 mb-3">Designed For</h2>
              <p className="lead">
                {brand.displayName} products are specifically crafted to meet the needs of these market segments
              </p>
            </Col>
          </Row>
          <Row className="g-3 justify-content-center">
            {brand.targetMarket.map((market, idx) => (
              <Col lg={3} md={4} sm={6} key={idx}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Card className="market-card h-100">
                    <Card.Body className="p-4">
                      <div className={`market-icon-${brand.id}`}>
                        ✓
                      </div>
                      <h5>{market}</h5>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Products Section */}
      <section className="py-5">
        <Container>
          <Row className="mb-5">
            <Col lg={8} className="mx-auto text-center">
              <h2 className="display-5 mb-3">{brand.displayName} Product Range</h2>
              <p className="lead">
                Explore our complete {brand.displayName} collection - {products.length} premium products
                designed with your specific needs in mind
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {products.map((product, index) => (
              <Col lg={4} md={6} key={product.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="brand-product-card h-100">
                    <div className={`brand-product-image-wrapper ${brand.id}-border`}>
                      <img
                        src={product.image}
                        alt={product.name}
                        className="brand-product-image"
                      />
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <div className="mb-2">
                        <Badge className={`btn-brand-${brand.id} border-0`}>
                          {product.category}
                        </Badge>
                        <Badge bg="light" text="dark" className="ms-2">
                          {product.specs.ply} Ply
                        </Badge>
                      </div>
                      <h4 className="mb-3">{product.name}</h4>
                      <p className="text-muted mb-3 flex-grow-1">
                        {product.description}
                      </p>

                      {/* Key Specs */}
                      <div className="brand-product-specs mb-3">
                        <small className="d-block mb-1">
                          <strong>Material:</strong> {product.specs.material}
                        </small>
                        {product.specs.dimension && (
                          <small className="d-block mb-1">
                            <strong>Size:</strong> {product.specs.dimension}
                          </small>
                        )}
                        {product.specs.weight && (
                          <small className="d-block">
                            <strong>Weight:</strong> {product.specs.weight}
                          </small>
                        )}
                      </div>

                      <Link
                        to={product.link}
                        className={`btn btn-brand-${brand.id} w-100 mt-auto`}
                      >
                        View Details
                      </Link>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Quality Features */}
      <section className="quality-features-section py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h2 className="display-5 mb-4">Why Choose {brand.displayName}?</h2>
              <p className="lead mb-4">
                {brand.displayName} products deliver exceptional value through our commitment to quality and innovation.
              </p>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <span className={`quality-checkmark-${brand.id}`}>
                    ✓
                  </span>
                  <strong>Manufactured in ISO-certified facility</strong>
                </li>
                <li className="mb-3">
                  <span className={`quality-checkmark-${brand.id}`}>
                    ✓
                  </span>
                  <strong>Consistent quality across all products</strong>
                </li>
                <li className="mb-3">
                  <span className={`quality-checkmark-${brand.id}`}>
                    ✓
                  </span>
                  <strong>Reliable supply chain and distribution</strong>
                </li>
                <li className="mb-3">
                  <span className={`quality-checkmark-${brand.id}`}>
                    ✓
                  </span>
                  <strong>Competitive pricing for bulk orders</strong>
                </li>
                <li className="mb-3">
                  <span className={`quality-checkmark-${brand.id}`}>
                    ✓
                  </span>
                  <strong>Customizable options available</strong>
                </li>
              </ul>
            </Col>
            <Col lg={6}>
              <Card className="contact-card">
                <Card.Body className="p-5">
                  <h4 className="mb-4 text-center">Get {brand.displayName} Products</h4>
                  <div className="mb-4">
                    <div className="contact-info-item">
                      <strong>📞 Phone:</strong><br />
                      <a href="tel:+918920065965">+91 8920065965</a>
                    </div>
                    <div className="contact-info-item">
                      <strong>📧 Email:</strong><br />
                      <a href="mailto:sales@skytion.in">sales@skytion.in</a>
                    </div>
                    <div className="contact-info-item">
                      <strong>🏭 Location:</strong><br />
                      Noida, India
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    className={`btn btn-brand-${brand.id} w-100 btn-lg`}
                  >
                    Request Bulk Quote
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Manufacturing Process Infographic */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="mb-4">
            <Col lg={8} className="mx-auto text-center">
              <h3 className="mb-3">Our Manufacturing Process</h3>
              <p className="lead">
                From raw materials to premium {brand.displayName} products - quality at every stage
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg={10}>
              <Card className="border-0 shadow">
                <Card.Body className="p-4">
                  <img
                    src="/assets/skytion/brand/Process infographics.png"
                    alt="Manufacturing Process Infographic"
                    className="img-fluid rounded"
                    style={{ width: '100%', height: 'auto' }}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Quality Certifications Visual */}
      <section className="py-5">
        <Container>
          <Row className="mb-4">
            <Col lg={8} className="mx-auto text-center">
              <h3 className="mb-3">Quality Assurance & Certifications</h3>
              <p className="lead">
                Certified excellence in every {brand.displayName} product
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center align-items-center g-4">
            <Col md={4} className="text-center">
              <img
                src="/assets/skytion/brand/certification badge .png"
                alt="Quality Certification"
                className="img-fluid"
                style={{ maxWidth: '200px', height: 'auto' }}
              />
            </Col>
            <Col md={4} className="text-center">
              <img
                src="/assets/skytion/brand/premium quality tag.png"
                alt="Premium Quality"
                className="img-fluid"
                style={{ maxWidth: '200px', height: 'auto' }}
              />
            </Col>
            <Col md={4} className="text-center">
              <img
                src="/assets/skytion/brand/GOLD_RIM-removebg-preview.png"
                alt="Quality Award"
                className="img-fluid"
                style={{ maxWidth: '200px', height: 'auto' }}
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Other Brands */}
      <section className="other-brands-section py-5 bg-light">
        <Container>
          <Row className="text-center mb-4">
            <Col>
              <h3 className="mb-3">Explore Our Other Brands</h3>
              <p className="text-muted">
                Skytion Enterprises offers a complete portfolio to serve every market segment
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={10}>
              <div className="other-brands-buttons">
                {(['SKYSUN', 'SKYTION'] as const)
                  .filter(b => b !== brandName)
                  .map(otherBrand => {
                    const otherBrandInfo = getBrandInfo(otherBrand);
                    return (
                      <Link
                        key={otherBrand}
                        to={`/brands/${otherBrandInfo.id}`}
                        className="btn btn-outline-primary btn-lg"
                      >
                        {otherBrandInfo.displayName}
                      </Link>
                    );
                  })}
                <Link to="/brands" className="btn btn-primary btn-lg">
                  View All Brands
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default BrandPage;
